import { useLayoutEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BarChartScene } from '@/components/three/BarChartScene';
import { SplitLines } from '@/components/SplitLines';
import { TIMELINE, CHART_BARS } from '@/data/content';
import { Counter } from '@/components/Counter';
import { usePerfTier } from '@/lib/usePerfTier';

gsap.registerPlugin(ScrollTrigger);

export const Timeline = () => {
  const sectionRef = useRef(null);
  const lineRef = useRef(null);
  const progressRef = useRef(0);
  const { dpr } = usePerfTier();

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: 'top 70%',
        end: 'bottom bottom',
        onUpdate: (self) => {
          progressRef.current = self.progress;
          if (lineRef.current) lineRef.current.style.transform = `scaleY(${self.progress})`;
        },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="milestones" data-testid="timeline-section" className="relative bg-void section-pad py-28 md:py-40 border-t border-ink/10">
      <div className="mb-16 md:mb-24">
        <p className="text-[11px] uppercase tracking-[0.4em] text-brand-gold mb-5">04 — Milestones</p>
        <h2 className="font-clash font-semibold text-ink tracking-tight leading-[1.05] text-4xl md:text-5xl">
          <SplitLines inView lines={['A legacy measured', <span key="a" className="text-ink/70">acre by acre.</span>]} />
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        {/* Timeline */}
        <div className="relative pl-8">
          <div className="absolute left-0 top-2 bottom-2 w-px bg-ink/10" />
          <div
            ref={lineRef}
            className="absolute left-0 top-2 bottom-2 w-px bg-gradient-to-b from-brand-gold to-brand-royal origin-top"
            style={{ transform: 'scaleY(0)', boxShadow: '0 0 12px rgba(224,168,46,0.5)' }}
          />
          <div className="space-y-14">
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.phase}
                data-testid={`timeline-item-${i}`}
                initial={{ opacity: 0, x: -28 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-10%' }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="relative"
              >
                <span className="absolute -left-[37px] top-1.5 w-[9px] h-[9px] rounded-full bg-brand-gold" style={{ boxShadow: '0 0 12px rgba(224,168,46,0.9)' }} />
                <p className="text-[10px] uppercase tracking-[0.3em] text-brand-gold">{t.phase}</p>
                <h3 className="mt-2 font-clash font-medium text-xl md:text-2xl text-ink">{t.title}</h3>
                <p className="mt-2 text-xs md:text-sm text-ink/70 leading-relaxed max-w-md">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 3D bar chart */}
        <div className="lg:sticky lg:top-24 rounded-2xl overflow-hidden border border-ink/10 self-start bg-void/50">
          <div className="h-[220px] sm:h-[300px] md:h-[520px] relative">
            <BarChartScene progressRef={progressRef} dpr={dpr} />
            <div className="absolute bottom-3 left-4 md:bottom-4 md:left-5 pointer-events-none hidden md:block">
              <p className="text-[10px] uppercase tracking-[0.3em] text-ink/55">Corporate statistics</p>
            </div>
          </div>
          {/* Mobile statistics grid to prevent 3D Html text overlap */}
          <div className="p-3 bg-void/90 border-t border-ink/10 grid grid-cols-5 gap-1 text-center md:hidden">
            {CHART_BARS.map((b) => (
              <div key={b.label} className="flex flex-col items-center">
                <p className="font-clash font-semibold text-ink text-xs leading-none">
                  <Counter to={b.value} suffix={b.suffix} start duration={2.2} />
                </p>
                <p className="text-[7.5px] uppercase tracking-wider text-ink/60 mt-1 truncate max-w-full">{b.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
